'use client'

import React, { useState, useEffect } from 'react'
import { createClient } from '@/lib/supabase/client'
import { getCurrentUser, getUserProfile } from '@/lib/supabase-auth'
import { ChevronDown, ChevronUp, User, Shield, Key, Clock, Database, Globe, AlertCircle } from 'lucide-react'
import { UntitledBadge } from '@/components/ui/untitled-badge'
import { Button } from '@/components/ui/button'

interface DebugInfo {
  authUser: any
  profile: any
  session: any
  cookies: string[]
  localStorage: any
  timestamp: string
  errors: string[]
}

export function UserDebugPanel() {
  const [isOpen, setIsOpen] = useState(false)
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null)
  const [loading, setLoading] = useState(false)
  const supabase = createClient()

  const fetchDebugInfo = async () => {
    setLoading(true)
    const errors: string[] = []
    const info: DebugInfo = {
      authUser: null,
      profile: null,
      session: null,
      cookies: [],
      localStorage: {},
      timestamp: new Date().toISOString(),
      errors: []
    }

    try {
      // Get auth user
      const { data: { user }, error: userError } = await supabase.auth.getUser()
      if (userError) {
        errors.push(`Auth User Error: ${userError.message}`)
      } else {
        info.authUser = user
      }

      // Get session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError) {
        errors.push(`Session Error: ${sessionError.message}`)
      } else {
        info.session = session
      }

      // Get profile if user exists
      if (user) {
        try {
          const profile = await getUserProfile(user.id)
          info.profile = profile
        } catch (profileError: any) {
          errors.push(`Profile Error: ${profileError?.message || 'Unknown error'}`)
        }
      }

      // Get cookies (safe check for SSR)
      if (typeof document !== 'undefined') {
        info.cookies = document.cookie.split(';').map(c => c.trim())
      }

      // Get localStorage items (safe check for SSR)
      if (typeof window !== 'undefined' && window.localStorage) {
        const localStorageData: any = {}
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (key && (key.includes('supabase') || key.includes('auth'))) {
            try {
              const value = localStorage.getItem(key)
              localStorageData[key] = value ? JSON.parse(value) : null
            } catch {
              localStorageData[key] = localStorage.getItem(key)
            }
          }
        }
        info.localStorage = localStorageData
      }

      info.errors = errors
    } catch (error: any) {
      errors.push(`General Error: ${error?.message || 'Unknown error'}`)
      info.errors = errors
    }

    setDebugInfo(info)
    setLoading(false)
  }

  useEffect(() => {
    if (isOpen && !debugInfo) {
      fetchDebugInfo()
    }
  }, [isOpen])

  // Only show in development or if debug mode is enabled
  const isDevelopment = process.env.NODE_ENV === 'development'
  const isDebugMode = typeof window !== 'undefined' && 
    (window.location.search.includes('debug=true') || 
     localStorage.getItem('debugMode') === 'true')

  if (!isDevelopment && !isDebugMode) {
    return null
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-md">
      <div className="bg-base-100 rounded-lg shadow-xl border border-base-300">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-4 py-2 flex items-center justify-between text-sm font-medium hover:bg-base-200 rounded-t-lg"
        >
          <div className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <span>User Debug Panel</span>
            {debugInfo?.errors && debugInfo.errors.length > 0 && (
              <UntitledBadge variant="modern">{debugInfo.errors.length}</UntitledBadge>
            )}
          </div>
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        </button>

        {isOpen && (
          <div className="max-h-[600px] overflow-y-auto p-4 space-y-4 text-xs">
            <div className="flex items-center justify-between">
              <Button onClick={fetchDebugInfo} variant="modern" size="sm" className="h-7 text-xs" disabled={loading}>
                {loading ? 'Refreshing...' : 'Refresh'}
              </Button>
              <span className="text-base-content/60">
                {debugInfo?.timestamp && new Date(debugInfo.timestamp).toLocaleTimeString()}
              </span>
            </div>

            {debugInfo?.errors && debugInfo.errors.length > 0 && (
              <div className="alert alert-error">
                <AlertCircle className="h-4 w-4" />
                <div>
                  <h4 className="font-bold">Errors Detected</h4>
                  {debugInfo.errors.map((error, i) => (
                    <p key={i} className="text-xs">{error}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Auth User Section */}
            <div className="space-y-2">
              <h3 className="font-bold flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Auth User
              </h3>
              {debugInfo?.authUser ? (
                <div className="bg-base-200 p-2 rounded space-y-1">
                  <div><span className="font-semibold">ID:</span> {debugInfo.authUser.id}</div>
                  <div><span className="font-semibold">Email:</span> {debugInfo.authUser.email}</div>
                  <div><span className="font-semibold">Provider:</span> {debugInfo.authUser.app_metadata?.provider || 'email'}</div>
                  <div><span className="font-semibold">Confirmed:</span> {debugInfo.authUser.confirmed_at ? '✅' : '❌'}</div>
                  <div><span className="font-semibold">Last Sign In:</span> {debugInfo.authUser.last_sign_in_at ? new Date(debugInfo.authUser.last_sign_in_at).toLocaleString() : 'Never'}</div>
                </div>
              ) : (
                <div className="alert alert-warning">No authenticated user</div>
              )}
            </div>

            {/* Profile Section */}
            <div className="space-y-2">
              <h3 className="font-bold flex items-center gap-2">
                <Database className="h-4 w-4" />
                User Profile
              </h3>
              {debugInfo?.profile ? (
                <div className="bg-base-200 p-2 rounded space-y-1">
                  <div><span className="font-semibold">Name:</span> {debugInfo.profile.first_name} {debugInfo.profile.last_name}</div>
                  <div><span className="font-semibold">Role:</span> <UntitledBadge variant="modern-inset">{debugInfo.profile.role}</UntitledBadge></div>
                  <div><span className="font-semibold">Active:</span> {debugInfo.profile.is_active ? '✅' : '❌'}</div>
                  <div><span className="font-semibold">Last Login:</span> {debugInfo.profile.last_login_at ? new Date(debugInfo.profile.last_login_at).toLocaleString() : 'Never'}</div>
                </div>
              ) : (
                <div className="alert alert-warning">No profile found</div>
              )}
            </div>

            {/* Session Section */}
            <div className="space-y-2">
              <h3 className="font-bold flex items-center gap-2">
                <Key className="h-4 w-4" />
                Session
              </h3>
              {debugInfo?.session ? (
                <div className="bg-base-200 p-2 rounded space-y-1">
                  <div><span className="font-semibold">Valid:</span> ✅</div>
                  <div><span className="font-semibold">Expires:</span> {new Date(debugInfo.session.expires_at * 1000).toLocaleString()}</div>
                  <div><span className="font-semibold">Provider:</span> {debugInfo.session.user?.app_metadata?.provider || 'email'}</div>
                  <div className="break-all">
                    <span className="font-semibold">Token:</span> 
                    <span className="text-xs opacity-60">{debugInfo.session.access_token?.substring(0, 20)}...</span>
                  </div>
                </div>
              ) : (
                <div className="alert alert-warning">No active session</div>
              )}
            </div>

            {/* Cookies Section */}
            <div className="space-y-2">
              <h3 className="font-bold flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Auth Cookies ({debugInfo?.cookies?.filter(c => c.includes('supabase') || c.includes('auth')).length || 0})
              </h3>
              <div className="bg-base-200 p-2 rounded space-y-1 max-h-32 overflow-y-auto">
                {debugInfo?.cookies?.filter(c => c.includes('supabase') || c.includes('auth')).map((cookie, i) => (
                  <div key={i} className="text-xs break-all opacity-80">
                    {cookie.substring(0, 50)}...
                  </div>
                )) || <div>No auth cookies found</div>}
              </div>
            </div>

            {/* LocalStorage Section */}
            <div className="space-y-2">
              <h3 className="font-bold flex items-center gap-2">
                <Clock className="h-4 w-4" />
                LocalStorage Keys
              </h3>
              <div className="bg-base-200 p-2 rounded space-y-1 max-h-32 overflow-y-auto">
                {Object.keys(debugInfo?.localStorage || {}).length > 0 ? (
                  Object.keys(debugInfo.localStorage).map((key, i) => (
                    <div key={i} className="text-xs opacity-80">{key}</div>
                  ))
                ) : (
                  <div>No auth localStorage items found</div>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="space-y-2 pt-2 border-t">
              <h3 className="font-bold">Quick Actions</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={async () => {
                    const { error } = await supabase.auth.refreshSession()
                    if (error) {
                      alert(`Failed to refresh: ${error.message}`)
                    } else {
                      alert('Session refreshed successfully!')
                      fetchDebugInfo()
                    }
                  }}
                  className="btn btn-xs"
                >
                  Refresh Session
                </button>
                <button
                  onClick={() => {
                    if (debugInfo) {
                      const debugData = JSON.stringify(debugInfo, null, 2)
                      navigator.clipboard.writeText(debugData)
                      alert('Debug info copied to clipboard!')
                    }
                  }}
                  className="btn btn-xs"
                >
                  Copy Debug Info
                </button>
                <button
                  onClick={() => {
                    localStorage.setItem('debugMode', localStorage.getItem('debugMode') === 'true' ? 'false' : 'true')
                    window.location.reload()
                  }}
                  className="btn btn-xs btn-outline"
                >
                  Toggle Debug Mode
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}